import { useQuery } from 'react-query';
import { useApi } from 'hooks/useApi';
import { useUser } from 'state/auth';
import { FilterViewEnum } from 'types/FilterProjects';
import { PaginatedProject, PaginateReponse } from 'types/index';
import type { ProjectFilterRequest } from '@mercator-gcp/types/elastic_search_service/ProjectFilterRequest';

const defaultPaginatedResponse = {
  results: [],
  count: 0,
};

type TGroupConfigLeadsType = {
  groupConfigId: number;
  currentPage: number;
  sortArray: string[];
  activeTabs?: FilterViewEnum[];
  companyIds?: number[];
  keywords?: ProjectFilterRequest['keywords'];
  enabled?: boolean;
};

export const useFilterGroupConfigLeads = ({
  groupConfigId,
  currentPage,
  sortArray,
  activeTabs = [],
  companyIds,
  keywords,
  enabled = true,
}: TGroupConfigLeadsType) => {
  const api = useApi();
  const { token } = useUser();

  const getFilteredLeads = useQuery(
    [
      'filtered-group-config-leads',
      groupConfigId,
      currentPage,
      sortArray,
      activeTabs,
      companyIds,
      token,
      keywords,
    ],
    async (): Promise<PaginateReponse<PaginatedProject[]>> => {
      if (!groupConfigId) {
        return defaultPaginatedResponse;
      }

      // Determine which exclude properties to set based on activeTabs
      const excludeProperties =
        activeTabs.length === 0 || activeTabs.includes(FilterViewEnum.ALL)
          ? {
              include_starred_projects: false,
              include_watching_projects: false,
              include_no_status_projects: false,
              include_hidden_projects: false,
            }
          : {
              include_starred_projects: activeTabs.includes(FilterViewEnum.STAR),
              include_watching_projects: activeTabs.includes(FilterViewEnum.WATCH),
              include_no_status_projects: activeTabs.includes(FilterViewEnum.NO_STATUS),
              include_hidden_projects: activeTabs.includes(FilterViewEnum.HIDDEN),
            };

      try {
        const response = await api.post<PaginateReponse<PaginatedProject[]>>(
          `group-configuration/${groupConfigId}/filter/?page=${currentPage}`,
          {
            sort: sortArray,
            companies: companyIds,
            keywords,
            ...excludeProperties,
          },
        );

        return response || defaultPaginatedResponse;
      } catch {
        return defaultPaginatedResponse;
      }
    },
    {
      refetchOnWindowFocus: false,
      enabled: enabled && Boolean(groupConfigId),
    },
  );

  return {
    getFilteredLeads,
  };
};
